// GRAVY-10808: the relative-timestamp rule for activity feeds, in ONE place.
//
// Product's elapsed-time table (GRAVY-10358 §C1) shipped inside DashboardV2's
// `ActivityItem`, so the Worker Profile dependability feed grew its own
// day-granularity formatter instead — a same-day event read "Today" where the
// AC (and the design) call for "Just now". GRAVY-10345 asks for one rule across
// all recent-activity UI, which it cannot be while each surface owns a copy.
//
// This module deliberately imports NOTHING. `@/util/date` transitively requires
// App.tsx (and @ionic/react's ESM build), which jest cannot parse — so a feed
// component importing the rule from there would take its whole test suite down
// with it.

/**
 * The row's primary timestamp — Product's table, verbatim (GRAVY-10358 §C1):
 *
 *   0–59 sec            Just now
 *   1–59 min            N min ago
 *   1–23 hr             N hr ago
 *   24–47 hr            1 day ago
 *   48 hr – same year   Aug 14
 *   48 hr – other year  Aug 14, 2025
 *
 * Two rules that are easy to get wrong and are asserted separately:
 *
 *   ROUND DOWN, never up. 89 seconds is "1 min ago", not "2 min ago" —
 *   `Math.floor` throughout, so the label never claims more elapsed time than
 *   has actually passed.
 *
 *   The UNIT is the abbreviation and does not inflect: "1 min ago" and
 *   "5 min ago". The previous build wrote "1 mins ago" for a whole minute and
 *   "15 mins ago" thereafter, which is the pluralisation the ticket calls out.
 *
 * Note 2–6 days is NOT "N days ago": only 24–47 hours is a "day", and past that
 * the calendar date is what a reader can actually place, with a year once the
 * year differs.
 *
 * The exact moment is never lost — see `exactTime`, which every row exposes on
 * hover.
 */
export const elapsedLabel = (
  iso?: string | null,
  now: Date = new Date(),
): string => {
  if (!iso) return '';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '';

  const seconds = Math.floor((now.getTime() - then.getTime()) / 1000);
  // A clock skew between server and browser must not print "in 3 min".
  if (seconds < 60) return 'Just now';

  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins} min ago`;

  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;

  // 24–47 hours only. From 48 the date is what places the event.
  if (hrs < 48) return '1 day ago';

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    // Older than the current year, so "Aug 14" would be ambiguous. Compared on
    // the YEAR rather than on a 365-day span: two dates four months apart can
    // straddle New Year, and "Aug 14" for last August is exactly the ambiguity
    // the year is there to remove.
    ...(then.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  }).format(then);
};

/**
 * The full moment, always available on hover (GRAVY-10358 §C1) — including for
 * "Just now" and "5 min ago", which is the point: a relative label is easier to
 * read and useless for a dispute, and the exact time is the opposite.
 *
 * "Aug 14, 2025, 4:15 PM PDT" — the viewer's own zone, NAMED. Without
 * `timeZoneName` an admin in another region reads a time that looks local and
 * is not, which is the one way this can mislead rather than merely omit.
 */
export const exactTime = (iso?: string | null): string | undefined => {
  if (!iso) return undefined;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return undefined;
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(d);
};
